import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Stack,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  MenuItem,
  Select,
  Paper,
  Chip,
  Card,
  CardContent,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  AddRounded,
  SaveRounded,
  DeleteRounded,
  AutoFixHighRounded,
  DomainRounded,
  PublicRounded,
  BusinessRounded,
  CheckCircleRounded,
  ContentCopyRounded,
  QrCodeRounded,
  LinkRounded,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import MaterialTable from 'material-table';
import { useSelector, useDispatch } from 'react-redux';
import CircularLoading from '../components/CircularLoading';
import AlertDialog from '../components/AlertDialog';
import { useTranslation } from 'react-i18next';
import { api } from 'common';
import PartnerLanding from '../components/PartnerLanding.jsx';
import PartnerMaintenancePage from './PartnerMaintenancePage';
import PartnerComingSoonPage from './PartnerComingSoonPage';
import {
  normalizePartnerSlug,
  PARTNER_WIDGET_TYPES,
  PARTNER_WIDGET_PRESETS,
  getPartnerWidgets,
  getPartnerDisplayName,
} from '../common/partnerUtils';
import { getUserScope, hasPermission } from '../common/permissionUtils';

const WIDGET_LABELS = {
  hero: 'Hero',
  stats: 'Stats',
  fleet: 'Fleet',
  coverage: 'Coverage',
  pricing: 'Pricing',
  testimonials: 'Testimonials',
  safety: 'Safety',
  cta: 'Call to action',
  apps: 'App promo',
};

const WIDGET_DESCRIPTIONS = {
  hero: 'Primary headline, CTA, and brand promise.',
  stats: 'KPIs and credibility tiles.',
  fleet: 'Vehicle lineup and features.',
  coverage: 'Zones, service map, and areas.',
  pricing: 'Subscription or plan cards.',
  testimonials: 'Partner quotes and trust signals.',
  safety: 'Compliance and safety highlights.',
  cta: 'Conversion block for activation.',
  apps: 'App store promo badges.',
};

const presetLabels = {
  default: 'Default',
  corporate: 'Corporate',
  luxury: 'Luxury',
  eco: 'Eco',
};

const PARTNER_TEMPLATES = [
  {
    key: 'executive',
    label: 'Executive',
    description: 'Clean corporate layout for business travel and accounts.',
    widgetPreset: 'corporate',
    gradient: 'linear-gradient(135deg, rgba(15,23,42,0.95), rgba(2,132,199,0.7))',
    theme: {
      primary: '#0f172a',
      secondary: '#111827',
      accent: '#38bdf8',
      heroFrom: '#0f172a',
      heroTo: '#1e3a8a',
      background: '#f8fafc',
      surface: '#ffffff',
      text: '#0b0b0b',
      muted: '#64748b',
      fontHeading: '"Space Grotesk", "Sora", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontBody: '"Manrope", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    },
  },
  {
    key: 'heritage',
    label: 'Heritage',
    description: 'Premium service look with warm tones and strong trust cues.',
    widgetPreset: 'luxury',
    gradient: 'linear-gradient(135deg, rgba(120,53,15,0.95), rgba(251,191,36,0.75))',
    theme: {
      primary: '#78350f',
      secondary: '#92400e',
      accent: '#f59e0b',
      heroFrom: '#78350f',
      heroTo: '#b45309',
      background: '#fffbeb',
      surface: '#ffffff',
      text: '#1f2937',
      muted: '#92400e',
      fontHeading: '"Playfair Display", "DM Serif Display", serif',
      fontBody: '"Inter", "Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    },
  },
  {
    key: 'urban',
    label: 'Urban',
    description: 'High-energy city vibe with bright highlights.',
    widgetPreset: 'default',
    gradient: 'linear-gradient(135deg, rgba(2,132,199,0.95), rgba(56,189,248,0.75))',
    theme: {
      primary: '#0ea5e9',
      secondary: '#0284c7',
      accent: '#f97316',
      heroFrom: '#0ea5e9',
      heroTo: '#0369a1',
      background: '#f0f9ff',
      surface: '#ffffff',
      text: '#0f172a',
      muted: '#64748b',
      fontHeading: '"Sora", "Space Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontBody: '"Inter", "Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    },
  },
  {
    key: 'eco',
    label: 'Eco',
    description: 'Sustainable focus with calm greens and airy layout.',
    widgetPreset: 'eco',
    gradient: 'linear-gradient(135deg, rgba(16,185,129,0.95), rgba(34,197,94,0.75))',
    theme: {
      primary: '#10b981',
      secondary: '#059669',
      accent: '#84cc16',
      heroFrom: '#10b981',
      heroTo: '#047857',
      background: '#f0fdf4',
      surface: '#ffffff',
      text: '#0f172a',
      muted: '#4b5563',
      fontHeading: '"Space Grotesk", "Sora", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontBody: '"Inter", "Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    },
  },
];

const isValidJson = (value) => {
  if (!value) return true;
  try {
    JSON.parse(value);
    return true;
  } catch (e) {
    return false;
  }
};

const parseCsv = (value) =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const formatCsv = (items) => (items && items.length ? items.join(', ') : '');

const toDomainList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return parseCsv(String(value));
};

const parseLines = (value) =>
  value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

const formatLines = (items) => (items && items.length ? items.join('\n') : '');

const buildRow = (partner) => {
  const domains = Array.isArray(partner?.domains)
    ? partner.domains.join(', ')
    : partner?.domain || '';
  return {
    slug: partner?.slug || '',
    name: partner?.name || partner?.companyName || '',
    active: partner?.active !== false,
    fleetadmin: partner?.fleetadmin || '',
    subdomain: partner?.subdomain || '',
    domains,
    domainMode: partner?.domainMode || partner?.domain_mode || 'both',
    maintenanceMode: !!partner?.maintenanceMode,
    _partner: partner || {},
  };
};

const normalizePartner = (partner) => {
  const slug = normalizePartnerSlug(partner.slug || '');
  return {
    ...partner,
    slug,
    name: partner.name || partner.companyName || '',
    active: partner.active !== false,
    domainMode: partner.domainMode || partner.domain_mode || 'both',
    vanitySlug: partner.vanitySlug || partner.publicSlug || '',
    maintenanceMode: !!partner.maintenanceMode,
    maintenanceMessage: partner.maintenanceMessage || '',
    comingSoonMode: !!partner.comingSoonMode || !!partner.comingSoon,
    comingSoonMessage: partner.comingSoonMessage || '',
    logoUrl: partner.logoUrl || partner.logo || '',
    faviconUrl: partner.faviconUrl || '',
    pageTitle: partner.pageTitle || partner.metaTitle || '',
    metaDescription: partner.metaDescription || partner.seoDescription || '',
    ogImage: partner.ogImage || partner.socialImage || '',
    supportEmail: partner.supportEmail || partner.contactEmail || '',
    supportPhone: partner.supportPhone || partner.contactPhone || '',
    supportAddress: partner.supportAddress || partner.contactAddress || '',
  };
};

const defaultPartner = (index) => ({
  slug: `partner-${index}`,
  name: `Partner ${index}`,
  active: true,
  domainMode: 'subdomain',
  vanitySlug: '',
  maintenanceMode: false,
  maintenanceMessage: '',
  comingSoonMode: false,
  comingSoonMessage: '',
  logoUrl: '',
  faviconUrl: '',
  pageTitle: '',
  metaDescription: '',
  ogImage: '',
  supportEmail: '',
  supportPhone: '',
  supportAddress: '',
  widgetPreset: 'default',
  enabledWidgets: [...(PARTNER_WIDGET_PRESETS.default || PARTNER_WIDGET_TYPES)],
  theme: {
    primary: '#0f172a',
    accent: '#f59e0b',
    heroFrom: '#0f172a',
    heroTo: '#111827',
    background: '#f8fafc',
    surface: '#ffffff',
    text: '#0b0b0b',
    muted: '#6b7280',
  },
});

const extractHost = (value) => {
  if (!value) return '';
  try {
    const url = new URL(value.includes('://') ? value : `https://${value}`);
    return url.hostname;
  } catch (e) {
    return value.toString().trim();
  }
};

const getBaseAppUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (
      hostname === 'localhost' ||
      hostname.endsWith('.localhost') ||
      /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)
    ) {
      return window.location.origin;
    }
  }
  return 'https://reaplnprge.web.app';
};

export default function PartnerSettings() {
  const { t } = useTranslation();
  const settingsdata = useSelector((state) => state.settingsdata);
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const { editSettings, clearSettingsViewError } = api;

  const [rows, setRows] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [draftPartner, setDraftPartner] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [domainsInput, setDomainsInput] = useState('');
  const [selectedWidgetType, setSelectedWidgetType] = useState('hero');
  const [themeJson, setThemeJson] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [qrDialog, setQrDialog] = useState(false);
  const [qrValue, setQrValue] = useState('');
  const [previewAsCustomer, setPreviewAsCustomer] = useState(false);
  const settings = useMemo(() => settingsdata.settings || {}, [settingsdata.settings]);
  const canReadPartners = hasPermission(auth.profile, settings, 'partners', 'read');
  const canCreatePartners = hasPermission(auth.profile, settings, 'partners', 'create');
  const canUpdatePartners = hasPermission(auth.profile, settings, 'partners', 'update');
  const canDeletePartners = hasPermission(auth.profile, settings, 'partners', 'delete');
  const scope = useMemo(() => getUserScope(auth.profile, settings), [auth.profile, settings]);
  const { allowedPartners: profileAllowedPartners, partnerSlug, partnerCode } = auth.profile || {};
  const allowedPartners = useMemo(() => {
    if (Array.isArray(profileAllowedPartners) && profileAllowedPartners.length) return profileAllowedPartners;
    if (partnerSlug) return [partnerSlug];
    if (partnerCode) return [partnerCode];
    return [];
  }, [profileAllowedPartners, partnerSlug, partnerCode]);
  const isReadOnly = !canUpdatePartners;
  const editBlockSx = isReadOnly ? { opacity: 0.7, pointerEvents: 'none' } : {};

  useEffect(() => {
    if (!settingsdata.settings) return;
    const partnerPages = settingsdata.settings.partnerPages || [];
    const list = Array.isArray(partnerPages) ? partnerPages : Object.values(partnerPages);
    const scoped = list.filter((partner) => {
      if (!canReadPartners) return false;
      if (scope === 'all') return true;
      const slug = normalizePartnerSlug(partner.slug || '');
      if (scope === 'fleet') {
        return partner.fleetadmin === auth.profile?.uid || allowedPartners.includes(slug);
      }
      if (scope === 'partner' || scope === 'self') {
        return allowedPartners.includes(slug);
      }
      return true;
    });
    const mapped = scoped.map((partner) => buildRow(normalizePartner(partner)));
    if (isDirty) {
      // Don't clobber in-progress edits (new partner / unsaved changes) with remote settings refreshes.
      return;
    }
    setRows(mapped);
    const hasSelection = mapped.some((row) => row.slug === selectedSlug);
    if ((!selectedSlug || !hasSelection) && mapped.length) {
      setSelectedSlug(mapped[0].slug);
      setDraftPartner(mapped[0]._partner);
      setDomainsInput(formatCsv(toDomainList(mapped[0]._partner?.domains || mapped[0]._partner?.domain || '')));
      setThemeJson(mapped[0]._partner?.theme ? JSON.stringify(mapped[0]._partner.theme, null, 2) : '');
      setIsDirty(false);
      return;
    }
    if (!isDirty && hasSelection) {
      const selected = mapped.find((row) => row.slug === selectedSlug);
      if (selected?._partner) {
        setDraftPartner(selected._partner);
        setDomainsInput(formatCsv(toDomainList(selected._partner?.domains || selected._partner?.domain || '')));
        setThemeJson(selected._partner?.theme ? JSON.stringify(selected._partner.theme, null, 2) : '');
      }
    }
  }, [settingsdata.settings, canReadPartners, scope, allowedPartners, auth.profile?.uid, isDirty, selectedSlug]);

  useEffect(() => {
    if (!settingsdata.loading && isSaving) {
      setIsSaving(false);
      setIsDirty(false);
    }
  }, [settingsdata.loading, isSaving]);

  useEffect(() => {
    if (!canReadPartners) {
      setRows([]);
      setSelectedSlug(null);
      setDraftPartner(null);
    }
  }, [canReadPartners]);

  const widgetOptions = useMemo(
    () => PARTNER_WIDGET_TYPES.map((type) => ({ type, label: WIDGET_LABELS[type] || type })),
    []
  );

  const baseHost = useMemo(() => {
    const website = settingsdata.settings?.CompanyWebsite || settingsdata.settings?.companyWebsite;
    return extractHost(website);
  }, [settingsdata.settings]);

  const baseAppUrl = useMemo(() => getBaseAppUrl(), []);

  const partnerStats = useMemo(() => {
    const total = rows.length;
    const active = rows.filter((row) => row.active).length;
    const subdomain = rows.filter(
      (row) => row.domainMode === 'subdomain' || row.domainMode === 'both'
    ).length;
    const standalone = rows.filter(
      (row) => row.domainMode === 'standalone' || row.domainMode === 'both'
    ).length;
    return { total, active, subdomain, standalone };
  }, [rows]);

  const widgetPreview = useMemo(() => {
    if (!draftPartner) return [];
    return getPartnerWidgets(draftPartner, settingsdata.settings);
  }, [draftPartner, settingsdata.settings]);

  const widgetMap = useMemo(() => {
    const map = {};
    widgetPreview.forEach((widget) => {
      map[widget.type] = widget;
    });
    return map;
  }, [widgetPreview]);

  const updateDraft = (patch) => {
    setIsDirty(true);
    setDraftPartner((prev) => ({ ...(prev || {}), ...patch }));
  };

  const updateTheme = (key, value) => {
    setIsDirty(true);
    setDraftPartner((prev) => ({
      ...(prev || {}),
      theme: { ...(prev?.theme || {}), [key]: value },
    }));
  };

  const updateWidgetContent = (type, patch) => {
    setIsDirty(true);
    setDraftPartner((prev) => {
      const current = prev?.widgetContent?.[type]?.data || widgetMap[type]?.data || {};
      return {
        ...(prev || {}),
        widgetContent: {
          ...(prev?.widgetContent || {}),
          [type]: {
            ...(prev?.widgetContent?.[type] || {}),
            data: { ...current, ...patch },
          },
        },
      };
    });
  };

  const handleCopy = async (value) => {
    if (!value) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const el = document.createElement('textarea');
        el.value = value;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
    } catch (e) {
      // Silent fail
    }
  };

  const openQrDialog = (value) => {
    if (!value) return;
    setQrValue(value);
    setQrDialog(true);
  };

  const enabledWidgets = useMemo(() => {
    const list = draftPartner?.enabledWidgets;
    if (Array.isArray(list) && list.length) return list;
    const preset = draftPartner?.widgetPreset || 'default';
    return PARTNER_WIDGET_PRESETS[preset] || PARTNER_WIDGET_PRESETS.default;
  }, [draftPartner]);

  const toggleWidget = (type) => {
    setIsDirty(true);
    const current = new Set(enabledWidgets || []);
    if (current.has(type)) {
      current.delete(type);
    } else {
      current.add(type);
    }
    updateDraft({ enabledWidgets: Array.from(current) });
  };

  const applyPreset = (preset) => {
    setIsDirty(true);
    const widgetList = PARTNER_WIDGET_PRESETS[preset] || PARTNER_WIDGET_PRESETS.default;
    updateDraft({ widgetPreset: preset, enabledWidgets: [...widgetList] });
  };

  const applyTemplate = (templateKey) => {
    const template = PARTNER_TEMPLATES.find((item) => item.key === templateKey);
    if (!template || !draftPartner) return;
    setIsDirty(true);
    const widgetList = PARTNER_WIDGET_PRESETS[template.widgetPreset] || PARTNER_WIDGET_PRESETS.default;
    updateDraft({
      landingTemplate: template.key,
      widgetPreset: template.widgetPreset,
      enabledWidgets: [...widgetList],
      theme: { ...(draftPartner.theme || {}), ...template.theme },
    });
    setThemeJson(JSON.stringify({ ...(draftPartner.theme || {}), ...template.theme }, null, 2));
  };

  const applyDummyContent = () => {
    if (!canUpdatePartners) return;
    if (!draftPartner) return;
    setIsDirty(true);
    const widgets = getPartnerWidgets(
      { ...draftPartner, enabledWidgets: enabledWidgets || PARTNER_WIDGET_PRESETS.default },
      settingsdata.settings
    );
    const widgetContent = {};
    widgets.forEach((widget) => {
      widgetContent[widget.type] = { data: widget.data };
    });
    updateDraft({ widgetContent });
  };

  const handleSelectRow = (rowData) => {
    const next = normalizePartner(rowData._partner || rowData);
    setSelectedSlug(next.slug);
    setDraftPartner(next);
    setDomainsInput(formatCsv(toDomainList(next.domains || next.domain || '')));
    setThemeJson(next.theme ? JSON.stringify(next.theme, null, 2) : '');
    setIsDirty(false);
  };

  const persistPartners = (partners) => {
    if (!canUpdatePartners) return;
    const updated = {
      ...settingsdata.settings,
      partnerPages: partners,
    };
    setIsSaving(true);
    dispatch(editSettings(updated));
  };

  const handleSave = () => {
    if (!canUpdatePartners) return;
    if (!draftPartner) return;
    if (!settingsdata.settings.AllowCriticalEditsAdmin) {
      alert(t('demo_mode') || 'Demo mode');
      return;
    }
    const normalizedSlug = normalizePartnerSlug(draftPartner.slug || '');
    if (!normalizedSlug) {
      alert(t('partner_slug_required') || 'Partner slug is required');
      return;
    }
    const duplicate = rows.some(
      (row) => row.slug === normalizedSlug && row.slug !== selectedSlug
    );
    if (duplicate) {
      alert(t('partner_slug_exists') || 'Partner slug already exists');
      return;
    }
    if (!isValidJson(themeJson)) {
      alert(t('theme_json_invalid') || 'Theme JSON is invalid');
      return;
    }
    const themeOverride = themeJson ? JSON.parse(themeJson) : {};
    const normalizedVanity = normalizePartnerSlug(draftPartner.vanitySlug || '');
    const normalizedSubdomain = normalizePartnerSlug(draftPartner.subdomain || '');
    const normalizedDomains = parseCsv(domainsInput || '');
    const partnerToSave = normalizePartner({
      ...draftPartner,
      slug: normalizedSlug,
      vanitySlug: normalizedVanity,
      subdomain: normalizedSubdomain,
      domains: normalizedDomains,
      theme: { ...(draftPartner.theme || {}), ...themeOverride },
      enabledWidgets: enabledWidgets || [],
    });
    const updatedRows = rows.map((row) =>
      row.slug === selectedSlug ? buildRow(partnerToSave) : row
    );
    const hasRow = rows.some((row) => row.slug === selectedSlug);
    const nextRows = hasRow ? updatedRows : [...updatedRows, buildRow(partnerToSave)];
    setRows(nextRows);
    setSelectedSlug(partnerToSave.slug);
    setDraftPartner(partnerToSave);
    persistPartners(nextRows.map((row) => row._partner));
  };

  const handleCreate = () => {
    if (!canCreatePartners) return;
    if (!settingsdata.settings.AllowCriticalEditsAdmin) {
      alert(t('demo_mode') || 'Demo mode');
      return;
    }
    const index = rows.length + 1;
    const partner = defaultPartner(index);
    const newRows = [...rows, buildRow(partner)];
    setRows(newRows);
    setSelectedSlug(partner.slug);
    setDraftPartner(partner);
    setDomainsInput(formatCsv(toDomainList(partner.domains || partner.domain || '')));
    setThemeJson(partner.theme ? JSON.stringify(partner.theme, null, 2) : '');
    setIsDirty(true);
  };

  const handleDelete = () => {
    if (!canDeletePartners) return;
    if (!draftPartner || !selectedSlug) return;
    if (!settingsdata.settings.AllowCriticalEditsAdmin) {
      alert(t('demo_mode') || 'Demo mode');
      return;
    }
    const confirmed = window.confirm('Delete this partner?');
    if (!confirmed) return;
    const newRows = rows.filter((row) => row.slug !== selectedSlug);
    setRows(newRows);
    setSelectedSlug(newRows[0]?.slug || null);
    setDraftPartner(newRows[0]?._partner || null);
    setDomainsInput(formatCsv(toDomainList(newRows[0]?._partner?.domains || newRows[0]?._partner?.domain || '')));
    persistPartners(newRows.map((row) => row._partner));
  };

  const currentWidgetData = widgetMap[selectedWidgetType]?.data || {};
  const partnerName = draftPartner
    ? getPartnerDisplayName(draftPartner, settingsdata.settings)
    : 'Partner';

  const previewPartner = useMemo(() => {
    if (!draftPartner) return null;
    if (previewAsCustomer) return draftPartner;
    return {
      ...draftPartner,
      maintenanceMode: false,
      comingSoonMode: false,
    };
  }, [draftPartner, previewAsCustomer]);

  const showComingSoonPreview = !!(previewAsCustomer && previewPartner?.comingSoonMode);
  const showMaintenancePreview = !!(
    previewAsCustomer && !showComingSoonPreview && previewPartner?.maintenanceMode
  );

  const shareLinks = useMemo(() => {
    if (!draftPartner) return [];
    const links = [];
    const slugValue = normalizePartnerSlug(draftPartner.slug || '');
    const vanityValue = normalizePartnerSlug(draftPartner.vanitySlug || '');
    if (slugValue) {
      links.push({
        label: 'Path URL',
        value: `${baseAppUrl}/${slugValue}`,
      });
    }
    if (vanityValue && vanityValue !== slugValue) {
      links.push({
        label: 'Custom path URL',
        value: `${baseAppUrl}/${vanityValue}`,
      });
    }
    if (draftPartner.customUrl) {
      links.push({
        label: 'Custom URL',
        value: draftPartner.customUrl,
      });
    }
    const domains = Array.isArray(draftPartner.domains) ? draftPartner.domains : [];
    domains.forEach((domain) => {
      const host = extractHost(domain);
      if (host) {
        links.push({
          label: 'Standalone domain',
          value: `https://${host}`,
        });
      }
    });
    if (draftPartner.subdomain && baseHost) {
      links.push({
        label: 'Subdomain',
        value: `https://${draftPartner.subdomain}.${baseHost}`,
      });
    }
    const seen = new Set();
    return links.filter((link) => {
      if (!link.value) return false;
      if (seen.has(link.value)) return false;
      seen.add(link.value);
      return true;
    });
  }, [draftPartner, baseAppUrl, baseHost]);

  if (!settingsdata.settings) {
    return <CircularLoading />;
  }

  if (!canReadPartners) {
    return (
      <Paper sx={{ p: 4, borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t('no_access') || 'No access'}
        </Typography>
        <Typography sx={{ color: '#64748b', mt: 1 }}>
          {t('no_access_partners') || 'You do not have permission to view partner pages.'}
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Paper
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 4,
          border: '1px solid #e2e8f0',
          background:
            'linear-gradient(120deg, rgba(248,250,252,0.95) 0%, rgba(226,232,240,0.6) 40%, rgba(254,243,199,0.7) 100%)',
          boxShadow: '0 24px 40px rgba(15,23,42,0.08)',
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={3}
          alignItems={{ xs: 'flex-start', lg: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a' }}>
              Partner Studio
            </Typography>
            <Typography sx={{ color: '#475569', mt: 1 }}>
              Build high-converting partner pages with custom domains, themes, and widgets.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
              <Chip
                icon={<CheckCircleRounded />}
                label={`${partnerStats.active}/${partnerStats.total} active`}
                sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 600 }}
              />
              <Chip
                icon={<DomainRounded />}
                label={`${partnerStats.subdomain} subdomains`}
                sx={{ bgcolor: '#e0f2fe', color: '#075985', fontWeight: 600 }}
              />
              <Chip
                icon={<PublicRounded />}
                label={`${partnerStats.standalone} standalone`}
                sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600 }}
              />
            </Stack>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={handleCreate}
              disabled={!canCreatePartners}
            >
              {t('add') || 'Add'} Partner
            </Button>
            <Button
              variant="outlined"
              startIcon={<AutoFixHighRounded />}
              onClick={applyDummyContent}
              disabled={!draftPartner || !canUpdatePartners}
            >
              Load demo content
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<SaveRounded />}
              onClick={handleSave}
              disabled={!draftPartner || !canUpdatePartners}
            >
              Save changes
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteRounded />}
              onClick={handleDelete}
              disabled={!draftPartner || !canDeletePartners}
            >
              {t('delete') || 'Delete'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={2}>
        {[
          {
            label: 'Total partners',
            value: partnerStats.total,
            icon: BusinessRounded,
            color: '#0f172a',
            bg: '#e2e8f0',
          },
          {
            label: 'Active',
            value: partnerStats.active,
            icon: CheckCircleRounded,
            color: '#16a34a',
            bg: '#dcfce7',
          },
          {
            label: 'Subdomains',
            value: partnerStats.subdomain,
            icon: DomainRounded,
            color: '#0284c7',
            bg: '#e0f2fe',
          },
          {
            label: 'Standalone domains',
            value: partnerStats.standalone,
            icon: PublicRounded,
            color: '#d97706',
            bg: '#fef3c7',
          },
        ].map((item) => {
          const Icon = item.icon;
          return (
            <Grid item xs={12} sm={6} lg={3} key={item.label}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 14px 28px rgba(15,23,42,0.08)',
                }}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: item.bg, color: item.color }}>
                    <Icon />
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>
                      {item.value}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <MaterialTable
              title={t('partner_pages') || 'Partner Pages'}
              columns={[
                { title: t('partner_slug') || 'Slug', field: 'slug' },
                { title: t('partner_name') || 'Name', field: 'name' },
                { title: t('fleetadmin_uid') || 'Fleet Admin UID', field: 'fleetadmin' },
                { title: t('active') || 'Active', field: 'active', type: 'boolean' },
                { title: t('domain_mode') || 'Domain mode', field: 'domainMode' },
                { title: t('subdomain') || 'Subdomain', field: 'subdomain' },
                { title: t('domains') || 'Domains', field: 'domains' },
              ]}
              data={rows}
              onRowClick={(evt, rowData) => handleSelectRow(rowData)}
              options={{
                paging: true,
                search: true,
                exportButton: false,
                rowStyle: (rowData) => ({
                  backgroundColor: rowData.slug === selectedSlug ? '#f1f5f9' : '#fff',
                }),
                headerStyle: { fontWeight: 700 },
              }}
            />
          </Paper>

          {draftPartner ? (
            <Paper sx={{ mt: 3, p: 3, borderRadius: 3, border: '1px solid #e2e8f0', ...editBlockSx }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Partner basics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('partner_name') || 'Partner name'}
                    fullWidth
                    value={draftPartner.name || ''}
                    onChange={(e) => updateDraft({ name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('partner_slug') || 'Partner slug'}
                    fullWidth
                    value={draftPartner.slug || ''}
                    onChange={(e) => updateDraft({ slug: e.target.value })}
                    onBlur={(e) => updateDraft({ slug: normalizePartnerSlug(e.target.value) })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('partner_vanity') || 'Vanity path'}
                    fullWidth
                    value={draftPartner.vanitySlug || ''}
                    onChange={(e) => updateDraft({ vanitySlug: e.target.value })}
                    onBlur={(e) => updateDraft({ vanitySlug: normalizePartnerSlug(e.target.value) })}
                    helperText={t('partner_vanity_help') || 'Optional custom path for share links.'}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkRounded fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('fleetadmin_uid') || 'Fleet admin UID'}
                    fullWidth
                    value={draftPartner.fleetadmin || ''}
                    onChange={(e) => updateDraft({ fleetadmin: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('subdomain') || 'Subdomain'}
                    fullWidth
                    value={draftPartner.subdomain || ''}
                    onChange={(e) => updateDraft({ subdomain: e.target.value })}
                    onBlur={(e) => updateDraft({ subdomain: normalizePartnerSlug(e.target.value) })}
                    helperText={
                      baseHost
                        ? `Uses ${baseHost}`
                        : 'Uses your main domain (set CompanyWebsite)'
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label={t('domain_mode') || 'Domain mode'}
                    fullWidth
                    value={draftPartner.domainMode || 'both'}
                    onChange={(e) => updateDraft({ domainMode: e.target.value })}
                    helperText={t('domain_mode_help') || 'Choose subdomain, standalone domain, or both.'}
                  >
                    <MenuItem value="subdomain">
                      {t('domain_mode_subdomain') || 'Subdomain on main domain'}
                    </MenuItem>
                    <MenuItem value="standalone">
                      {t('domain_mode_standalone') || 'Standalone domain'}
                    </MenuItem>
                    <MenuItem value="both">{t('domain_mode_both') || 'Both'}</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={12}>
                  <TextField
                    label={t('domains') || 'Domains'}
                    fullWidth
                    value={domainsInput}
                    onChange={(e) => {
                      setDomainsInput(e.target.value);
                      setIsDirty(true);
                    }}
                    onBlur={(e) => {
                      const parsed = parseCsv(e.target.value || '');
                      setDomainsInput(formatCsv(parsed));
                      updateDraft({ domains: parsed });
                    }}
                    placeholder="citytaxi.yourdomain.com, rides.yourdomain.com"
                    helperText={
                      t('domain_mode_standalone_help') ||
                      'Standalone domains, comma separated.'
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t('custom_url') || 'Custom URL'}
                    fullWidth
                    value={draftPartner.customUrl || ''}
                    onChange={(e) => updateDraft({ customUrl: e.target.value })}
                    placeholder="https://custom-domain.com/booking"
                    helperText={t('custom_url_help') || 'Optional custom URL for QR codes and sharing.'}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={draftPartner.active !== false}
                        onChange={(e) => updateDraft({ active: e.target.checked })}
                      />
                    }
                    label={t('active') || 'Active'}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!draftPartner.maintenanceMode}
                        onChange={(e) => updateDraft({ maintenanceMode: e.target.checked })}
                      />
                    }
                    label={t('partner_maintenance_mode') || 'Maintenance mode'}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t('partner_maintenance_message') || 'Maintenance message'}
                    fullWidth
                    value={draftPartner.maintenanceMessage || ''}
                    onChange={(e) => updateDraft({ maintenanceMessage: e.target.value })}
                    disabled={!draftPartner.maintenanceMode}
                    placeholder="We are performing scheduled maintenance. Please check back soon."
                    helperText={
                      t('partner_maintenance_help') ||
                      'Shown to customers when maintenance is enabled.'
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!draftPartner.comingSoonMode}
                        onChange={(e) => updateDraft({ comingSoonMode: e.target.checked })}
                      />
                    }
                    label={t('partner_coming_soon_mode') || 'Coming soon mode'}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t('partner_coming_soon_message') || 'Coming soon message'}
                    fullWidth
                    value={draftPartner.comingSoonMessage || ''}
                    onChange={(e) => updateDraft({ comingSoonMessage: e.target.value })}
                    disabled={!draftPartner.comingSoonMode}
                    placeholder="We are launching soon. Stay tuned!"
                    helperText={
                      t('partner_coming_soon_help') ||
                      'Shown to customers when coming soon is enabled.'
                    }
                  />
                </Grid>
              </Grid>
            </Paper>
          ) : null}

          {draftPartner ? (
            <Paper sx={{ mt: 4, p: 3, borderRadius: 3, border: '1px solid #e2e8f0', ...editBlockSx }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Branding and SEO
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('partner_logo_url') || 'Logo URL'}
                    fullWidth
                    value={draftPartner.logoUrl || ''}
                    onChange={(e) => updateDraft({ logoUrl: e.target.value })}
                    placeholder="https://cdn.example.com/logo.png"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('partner_favicon_url') || 'Favicon URL'}
                    fullWidth
                    value={draftPartner.faviconUrl || ''}
                    onChange={(e) => updateDraft({ faviconUrl: e.target.value })}
                    placeholder="https://cdn.example.com/favicon.png"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t('partner_page_title') || 'Page title'}
                    fullWidth
                    value={draftPartner.pageTitle || ''}
                    onChange={(e) => updateDraft({ pageTitle: e.target.value })}
                    placeholder="Rydio Taxi | Premium rides"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t('partner_meta_description') || 'Meta description'}
                    fullWidth
                    multiline
                    minRows={2}
                    value={draftPartner.metaDescription || ''}
                    onChange={(e) => updateDraft({ metaDescription: e.target.value })}
                    placeholder="Fast pickups, business-ready rides, and premium support."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t('partner_og_image') || 'Social share image'}
                    fullWidth
                    value={draftPartner.ogImage || ''}
                    onChange={(e) => updateDraft({ ogImage: e.target.value })}
                    placeholder="https://cdn.example.com/og-image.png"
                  />
                </Grid>
              </Grid>
            </Paper>
          ) : null}

          {draftPartner ? (
            <Paper sx={{ mt: 4, p: 3, borderRadius: 3, border: '1px solid #e2e8f0', ...editBlockSx }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Partner contact details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('partner_support_email') || 'Support email'}
                    fullWidth
                    value={draftPartner.supportEmail || ''}
                    onChange={(e) => updateDraft({ supportEmail: e.target.value })}
                    placeholder="support@partner.com"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('partner_support_phone') || 'Support phone'}
                    fullWidth
                    value={draftPartner.supportPhone || ''}
                    onChange={(e) => updateDraft({ supportPhone: e.target.value })}
                    placeholder="+47 900 00 000"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t('partner_support_address') || 'Support address'}
                    fullWidth
                    value={draftPartner.supportAddress || ''}
                    onChange={(e) => updateDraft({ supportAddress: e.target.value })}
                    placeholder="Main street 1, Bergen"
                  />
                </Grid>
              </Grid>
            </Paper>
          ) : null}

          {draftPartner ? (
            <Paper sx={{ mt: 4, p: 3, borderRadius: 3, border: '1px solid #e2e8f0', ...editBlockSx }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                {t('landing_templates') || 'Landing templates'}
              </Typography>
              <Typography sx={{ color: '#64748b', mb: 3 }}>
                {t('landing_templates_subtitle') || 'Choose a layout and palette tailored to your partner.'}
              </Typography>
              <Grid container spacing={2}>
                {PARTNER_TEMPLATES.map((template) => (
                  <Grid item xs={12} md={6} key={template.key}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        borderColor: draftPartner.landingTemplate === template.key ? '#0f172a' : 'rgba(148,163,184,0.4)',
                        boxShadow: draftPartner.landingTemplate === template.key ? '0 18px 40px rgba(15,23,42,0.18)' : 'none',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': { transform: 'translateY(-2px)' },
                      }}
                    >
                      <Box sx={{ height: 120, borderRadius: 2, background: template.gradient, mb: 2 }} />
                      <Typography sx={{ fontWeight: 700 }}>{template.label}</Typography>
                      <Typography sx={{ color: '#64748b', fontSize: 13, mb: 2 }}>
                        {template.description}
                      </Typography>
                      <Button
                        variant={draftPartner.landingTemplate === template.key ? 'contained' : 'outlined'}
                        onClick={() => applyTemplate(template.key)}
                        disabled={!canUpdatePartners}
                      >
                        {draftPartner.landingTemplate === template.key
                          ? t('selected') || 'Selected'
                          : t('apply_template') || 'Apply template'}
                      </Button>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          ) : null}

          {draftPartner ? (
            <Paper sx={{ mt: 4, p: 3, borderRadius: 3, border: '1px solid #e2e8f0', ...editBlockSx }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Theme studio
              </Typography>
              <Grid container spacing={2}>
                {[
                  { key: 'primary', label: 'Primary' },
                  { key: 'accent', label: 'Accent' },
                  { key: 'heroFrom', label: 'Hero gradient from' },
                  { key: 'heroTo', label: 'Hero gradient to' },
                  { key: 'background', label: 'Background' },
                  { key: 'surface', label: 'Surface' },
                  { key: 'text', label: 'Text' },
                  { key: 'muted', label: 'Muted' },
                ].map((item) => (
                  <Grid item xs={12} md={6} key={item.key}>
                    <TextField
                      label={item.label}
                      fullWidth
                      value={draftPartner.theme?.[item.key] || ''}
                      onChange={(e) => updateTheme(item.key, e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <input
                            type="color"
                            value={draftPartner.theme?.[item.key] || '#000000'}
                            onChange={(e) => updateTheme(item.key, e.target.value)}
                            style={{ border: 'none', background: 'transparent' }}
                          />
                        ),
                      }}
                    />
                  </Grid>
                ))}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Heading font"
                    fullWidth
                    value={draftPartner.theme?.fontHeading || ''}
                    onChange={(e) => updateTheme('fontHeading', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Body font"
                    fullWidth
                    value={draftPartner.theme?.fontBody || ''}
                    onChange={(e) => updateTheme('fontBody', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t('theme_json') || 'Theme JSON'}
                    fullWidth
                    multiline
                    minRows={3}
                    value={themeJson}
                    onChange={(e) => {
                      setThemeJson(e.target.value);
                      setIsDirty(true);
                    }}
                    placeholder='{"primary":"#111827","accent":"#f59e0b"}'
                  />
                </Grid>
              </Grid>
            </Paper>
          ) : null}

          {draftPartner ? (
            <Paper sx={{ mt: 4, p: 3, borderRadius: 3, border: '1px solid #e2e8f0', ...editBlockSx }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Widget control
                </Typography>
                <Select
                  size="small"
                  value={draftPartner.widgetPreset || 'default'}
                  onChange={(e) => applyPreset(e.target.value)}
                >
                  {Object.keys(presetLabels).map((key) => (
                    <MenuItem key={key} value={key}>
                      {presetLabels[key]}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {widgetOptions.map((widget) => (
                  <Grid item xs={12} md={6} key={widget.type}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        background: 'linear-gradient(120deg, rgba(248,250,252,0.9), rgba(226,232,240,0.5))',
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>{widget.label}</Typography>
                        <Typography sx={{ fontSize: 13, color: '#64748b' }}>
                          {WIDGET_DESCRIPTIONS[widget.type]}
                        </Typography>
                      </Box>
                      <Switch
                        checked={enabledWidgets.includes(widget.type)}
                        onChange={() => toggleWidget(widget.type)}
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          ) : null}

          {draftPartner ? (
            <Paper sx={{ mt: 4, p: 3, borderRadius: 3, border: '1px solid #e2e8f0', ...editBlockSx }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Widget content editor
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                {widgetOptions.map((widget) => (
                  <Chip
                    key={widget.type}
                    label={widget.label}
                    color={selectedWidgetType === widget.type ? 'primary' : 'default'}
                    onClick={() => setSelectedWidgetType(widget.type)}
                    sx={{ mb: 1 }}
                  />
                ))}
              </Stack>
              <Divider sx={{ mb: 2 }} />
              {selectedWidgetType === 'hero' && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Hero title"
                      fullWidth
                      value={currentWidgetData.title || ''}
                      onChange={(e) => updateWidgetContent('hero', { title: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Hero subtitle"
                      fullWidth
                      multiline
                      minRows={2}
                      value={currentWidgetData.subtitle || ''}
                      onChange={(e) => updateWidgetContent('hero', { subtitle: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Primary CTA"
                      fullWidth
                      value={currentWidgetData.ctaPrimary || ''}
                      onChange={(e) => updateWidgetContent('hero', { ctaPrimary: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Secondary CTA"
                      fullWidth
                      value={currentWidgetData.ctaSecondary || ''}
                      onChange={(e) => updateWidgetContent('hero', { ctaSecondary: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Badges (one per line)"
                      fullWidth
                      multiline
                      minRows={2}
                      value={formatLines(currentWidgetData.badges || [])}
                      onChange={(e) =>
                        updateWidgetContent('hero', { badges: parseLines(e.target.value) })
                      }
                    />
                  </Grid>
                </Grid>
              )}
              {selectedWidgetType === 'stats' && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Stats title"
                      fullWidth
                      value={currentWidgetData.title || ''}
                      onChange={(e) => updateWidgetContent('stats', { title: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Stats subtitle"
                      fullWidth
                      value={currentWidgetData.subtitle || ''}
                      onChange={(e) => updateWidgetContent('stats', { subtitle: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Items (label|value per line)"
                      fullWidth
                      multiline
                      minRows={3}
                      value={formatLines(
                        (currentWidgetData.items || []).map((item) => `${item.label}|${item.value}`)
                      )}
                      onChange={(e) =>
                        updateWidgetContent('stats', {
                          items: parseLines(e.target.value).map((line) => {
                            const [label, value] = line.split('|');
                            return { label: label?.trim() || '', value: (value || '').trim() };
                          }),
                        })
                      }
                    />
                  </Grid>
                </Grid>
              )}
              {selectedWidgetType === 'fleet' && (
                <TextField
                  label="Fleet items (name|seats|feature1,feature2 per line)"
                  fullWidth
                  multiline
                  minRows={4}
                  value={formatLines(
                    (currentWidgetData.items || []).map(
                      (item) => `${item.name}|${item.seats}|${(item.features || []).join(',')}`
                    )
                  )}
                  onChange={(e) =>
                    updateWidgetContent('fleet', {
                      items: parseLines(e.target.value).map((line) => {
                        const [name, seats, features] = line.split('|');
                        return {
                          name: (name || '').trim(),
                          seats: (seats || '').trim(),
                          features: features ? parseCsv(features) : [],
                        };
                      }),
                    })
                  }
                />
              )}
              {selectedWidgetType === 'coverage' && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Coverage title"
                      fullWidth
                      value={currentWidgetData.title || ''}
                      onChange={(e) => updateWidgetContent('coverage', { title: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Areas (one per line)"
                      fullWidth
                      multiline
                      minRows={3}
                      value={formatLines(currentWidgetData.areas || [])}
                      onChange={(e) =>
                        updateWidgetContent('coverage', { areas: parseLines(e.target.value) })
                      }
                    />
                  </Grid>
                </Grid>
              )}
              {selectedWidgetType === 'pricing' && (
                <TextField
                  label="Plans (name|price|highlight(true/false)|feature1,feature2)"
                  fullWidth
                  multiline
                  minRows={4}
                  value={formatLines(
                    (currentWidgetData.plans || []).map(
                      (plan) =>
                        `${plan.name}|${plan.price}|${plan.highlight ? 'true' : 'false'}|${(
                          plan.features || []
                        ).join(',')}`
                    )
                  )}
                  onChange={(e) =>
                    updateWidgetContent('pricing', {
                      plans: parseLines(e.target.value).map((line) => {
                        const [name, price, highlight, features] = line.split('|');
                        return {
                          name: (name || '').trim(),
                          price: (price || '').trim(),
                          highlight: (highlight || '').trim() === 'true',
                          features: features ? parseCsv(features) : [],
                        };
                      }),
                    })
                  }
                />
              )}
              {selectedWidgetType === 'testimonials' && (
                <TextField
                  label="Testimonials (name|role|quote per line)"
                  fullWidth
                  multiline
                  minRows={4}
                  value={formatLines(
                    (currentWidgetData.items || []).map(
                      (item) => `${item.name}|${item.role}|${item.quote}`
                    )
                  )}
                  onChange={(e) =>
                    updateWidgetContent('testimonials', {
                      items: parseLines(e.target.value).map((line) => {
                        const [name, role, quote] = line.split('|');
                        return {
                          name: (name || '').trim(),
                          role: (role || '').trim(),
                          quote: (quote || '').trim(),
                        };
                      }),
                    })
                  }
                />
              )}
              {selectedWidgetType === 'safety' && (
                <TextField
                  label="Safety items (title|description per line)"
                  fullWidth
                  multiline
                  minRows={4}
                  value={formatLines(
                    (currentWidgetData.items || []).map(
                      (item) => `${item.title}|${item.desc}`
                    )
                  )}
                  onChange={(e) =>
                    updateWidgetContent('safety', {
                      items: parseLines(e.target.value).map((line) => {
                        const [title, desc] = line.split('|');
                        return { title: (title || '').trim(), desc: (desc || '').trim() };
                      }),
                    })
                  }
                />
              )}
              {selectedWidgetType === 'cta' && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="CTA title"
                      fullWidth
                      value={currentWidgetData.title || ''}
                      onChange={(e) => updateWidgetContent('cta', { title: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="CTA subtitle"
                      fullWidth
                      value={currentWidgetData.subtitle || ''}
                      onChange={(e) => updateWidgetContent('cta', { subtitle: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="CTA label"
                      fullWidth
                      value={currentWidgetData.ctaLabel || ''}
                      onChange={(e) => updateWidgetContent('cta', { ctaLabel: e.target.value })}
                    />
                  </Grid>
                </Grid>
              )}
              {selectedWidgetType === 'apps' && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Apps title"
                      fullWidth
                      value={currentWidgetData.title || ''}
                      onChange={(e) => updateWidgetContent('apps', { title: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Badges (comma separated)"
                      fullWidth
                      value={formatCsv(currentWidgetData.badges || [])}
                      onChange={(e) =>
                        updateWidgetContent('apps', { badges: parseCsv(e.target.value) })
                      }
                    />
                  </Grid>
                </Grid>
              )}
            </Paper>
          ) : null}

          {draftPartner ? (
            <Paper sx={{ mt: 4, p: 3, borderRadius: 3, border: '1px solid #e2e8f0', ...editBlockSx }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Share links and QR codes
              </Typography>
              {shareLinks.length ? (
                <Stack spacing={1.5}>
                  {shareLinks.map((link) => (
                    <Box
                      key={link.value}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 2,
                        alignItems: { md: 'center' },
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid #e2e8f0',
                        bgcolor: '#f8fafc',
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 600 }}>{link.label}</Typography>
                        <Typography sx={{ color: '#475569', wordBreak: 'break-all' }}>
                          {link.value}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Copy link">
                          <IconButton onClick={() => handleCopy(link.value)}>
                            <ContentCopyRounded />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Show QR code">
                          <IconButton onClick={() => openQrDialog(link.value)}>
                            <QrCodeRounded />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography sx={{ color: '#64748b' }}>
                  Add a slug, vanity path, or domain to generate share links.
                </Typography>
              )}
            </Paper>
          ) : null}
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper sx={{ borderRadius: 3, overflow: 'hidden', minHeight: 720 }}>
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid #e2e8f0',
                bgcolor: '#0f172a',
                color: '#fff',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { sm: 'center' },
                justifyContent: 'space-between',
                gap: 1.5,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700 }}>Live preview</Typography>
                <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
                  {partnerName} partner page
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={previewAsCustomer}
                    onChange={(e) => setPreviewAsCustomer(e.target.checked)}
                  />
                }
                label={t('preview_as_customer') || 'Preview as customer'}
              />
            </Box>
            <Box sx={{ maxHeight: '80vh', overflow: 'auto' }}>
              {previewPartner ? (
                showComingSoonPreview ? (
                  <PartnerComingSoonPage partner={previewPartner} disableMeta />
                ) : showMaintenancePreview ? (
                  <PartnerMaintenancePage partner={previewPartner} disableMeta />
                ) : (
                  <PartnerLanding partner={previewPartner} bookingPath="/addbookings" disableMeta />
                )
              ) : (
                <Box sx={{ p: 4 }}>
                  <Typography>Select a partner to preview.</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {settingsdata.error && settingsdata.error.flag ? (
        <AlertDialog
          open={settingsdata.error.flag}
          onClose={() => {
            setIsSaving(false);
            dispatch(clearSettingsViewError());
          }}
          title={t('alert')}
          message={settingsdata.error.msg?.message || settingsdata.error.msg}
        />
      ) : null}

      {settingsdata.loading && isSaving ? <CircularLoading /> : null}

      <Dialog open={qrDialog} onClose={() => setQrDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Partner QR code</DialogTitle>
        <DialogContent>
          <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
            {qrValue ? <QRCodeSVG value={qrValue} size={220} /> : null}
            <Typography sx={{ color: '#475569', textAlign: 'center', wordBreak: 'break-all' }}>
              {qrValue}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ContentCopyRounded />}
              onClick={() => handleCopy(qrValue)}
            >
              Copy link
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
